import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';


// Contentful configuration
const CONTENTFUL_SPACE_ID = 'l4m1dxs9e1m1';
const CONTENTFUL_ACCESS_TOKEN = 'DdxSj8Bsf5A1t15VEaQMVy70e_n4z8fHjVBK_2oZknw';
const CONTENTFUL_API_URL = `https://cdn.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/master/entries`;

const Blog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPosts = async () => {
            // Check if Contentful is configured
            if (CONTENTFUL_SPACE_ID === 'YOUR_SPACE_ID') {
                // Show demo content if not configured
                setPosts(getDemoPosts());
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${CONTENTFUL_API_URL}?content_type=blogPost&order=-sys.createdAt&include=2`,
                    {
                        headers: {
                            'Authorization': `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
                        },
                    }
                );

                if (!response.ok) throw new Error('Failed to fetch posts');

                const data = await response.json();
                const formattedPosts = data.items.map((item) => ({
                    id: item.sys.id,
                    title: item.fields.title,
                    slug: item.fields.slug,
                    excerpt: item.fields.excerpt || item.fields.body?.substring(0, 150) + '...',
                    body: item.fields.body,
                    publishDate: item.fields.publishDate || item.sys.createdAt,
                    featuredImage: item.fields.featuredImage 
                        ? `https:${data.includes?.Asset?.find(a => a.sys.id === item.fields.featuredImage.sys.id)?.fields.file.url}`
                        : 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
                    tags: item.fields.tags || [],
                }));

                setPosts(formattedPosts);
            } catch (err) {
                console.error('Error fetching blog posts:', err);
                setError('Failed to load blog posts');
                // Show demo content on error
                setPosts(getDemoPosts());
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    // Demo posts when Contentful is not configured
    const getDemoPosts = () => [
        {
            id: '1',
            title: 'The Ultimate Guide to Spa Treatments',
            slug: 'ultimate-guide-spa-treatments',
            excerpt: 'Discover the most popular spa treatments and their benefits. From Swedish massages to hot stone therapy, learn what each treatment offers.',
            publishDate: '2026-02-15',
            featuredImage: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80',
            tags: ['Wellness', 'Guide'],
        },
        {
            id: '2',
            title: 'Benefits of Ayurvedic Massage',
            slug: 'benefits-ayurvedic-massage',
            excerpt: 'Explore the ancient healing tradition of Ayurvedic massage and how it can improve your physical and mental well-being.',
            publishDate: '2026-02-10',
            featuredImage: 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=800&q=80',
            tags: ['Ayurveda', 'Health'],
        },
        {
            id: '3',
            title: 'Top 10 Spas in Mumbai for Couples',
            slug: 'top-spas-mumbai-couples',
            excerpt: 'Planning a romantic spa day? Here are the best spas in Mumbai that offer exceptional couples treatments and packages.',
            publishDate: '2026-02-05',
            featuredImage: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80',
            tags: ['Mumbai', 'Couples'],
        },
        {
            id: '4',
            title: 'How to Prepare for Your First Spa Visit',
            slug: 'prepare-first-spa-visit',
            excerpt: 'First time at a spa? Here\'s everything you need to know to make the most of your experience.',
            publishDate: '2026-02-01',
            featuredImage: 'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=800&q=80',
            tags: ['Tips', 'Beginners'],
        },
    ];

    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="blog-page">
            <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <span className="inline-block px-4 py-1.5 bg-[#722F37]/10 text-[#722F37] text-sm font-medium tracking-widest-custom uppercase rounded-full mb-4">
                        Our Blog
                    </span>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#4A4E4D] mb-4">
                        Wellness Insights
                    </h1>
                    <p className="text-[#4A4E4D]/70 max-w-2xl mx-auto">
                        Discover tips, guides, and stories about spa treatments, wellness practices, and self-care routines.
                    </p>
                </motion.div>

                {/* Contentful Connected */}

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="w-12 h-12 border-4 border-[#722F37]/20 border-t-[#722F37] rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {posts.map((post, index) => (
                            <motion.article
                                key={post.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="bg-white rounded-2xl overflow-hidden border border-[#E0DAD0] hover:border-[#722F37] transition-all duration-300 hover:shadow-lg group"
                            >
                                <Link to={`/blog/${post.slug}`}>
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img
                                            src={post.featuredImage}
                                            alt={post.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-4 text-sm text-[#4A4E4D]/60 mb-3">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                {new Date(post.publishDate).toLocaleDateString('en-IN', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })}
                                            </span>
                                        </div>
                                        <h2 className="font-heading text-xl text-[#4A4E4D] mb-3 group-hover:text-[#722F37] transition-colors line-clamp-2">
                                            {post.title}
                                        </h2>
                                        <p className="text-[#4A4E4D]/70 text-sm line-clamp-3 mb-4">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center gap-2 text-[#722F37] font-medium text-sm">
                                            Read More
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Blog;
